import React, { useState, useEffect } from 'react';
import { 
  Rocket, 
  Sparkles, 
  Layers, 
  TrendingUp, 
  ArrowRight, 
  CheckCircle2, 
  X, 
  ChevronRight,
  ChevronLeft,
  Loader2,
  Zap,
  Target,
  MousePointer2
} from 'lucide-react';
import { auth, db } from '../firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { useToast } from './Toast';
import { markChecklistItemCompleted } from '../src/lib/checklist';
import { executeFlowAction } from '../services/gemini';

interface LaunchWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onChangeView?: (view: any) => void;
}

type WizardStep = 'intro' | 'copy' | 'page' | 'performance' | 'finish';

export const LaunchWizard: React.FC<LaunchWizardProps> = ({ isOpen, onClose, onChangeView }) => {
  const { showToast } = useToast();
  const [step, setStep] = useState<WizardStep>('intro');
  const [loading, setLoading] = useState(false);
  
  // Form States
  const [productName, setProductName] = useState('');
  const [description, setDescription] = useState('');
  const [generatedCopy, setGeneratedCopy] = useState<any>(null);
  const [createdPageId, setCreatedPageId] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleGenerateCopy = async () => {
    if (!productName || !description) {
      showToast('Por favor, preencha o nome e a descrição do produto.', 'error');
      return;
    }

    setLoading(true);
    try {
      const context: any = {
        productName,
        description,
        currentHeadline: '',
        currentCTA: '',
        traffic: 0,
        conversionsA: 0,
        conversionsB: 0,
        ctrA: 0,
        ctrB: 0,
        bounceRate: 0,
        avgTimeOnPage: 0,
        daysActive: 0,
        totalLeads: 0,
        trafficSource: 'Todos',
        deviceType: 'Todos',
        plan: 'pro'
      };

      const rawResult = await executeFlowAction('gerar_copy_completa', context);
      
      // Sanitização robusta da resposta JSON da IA
      let cleaned = rawResult.trim();
      if (cleaned.startsWith('```json')) cleaned = cleaned.replace(/^```json\n?/, '').replace(/\n?```$/, '');
      else if (cleaned.startsWith('```')) cleaned = cleaned.replace(/^```\n?/, '').replace(/\n?```$/, '');
      
      const parsed = JSON.parse(cleaned);

      if (parsed.code === 'MISSING_API_KEY' || (parsed.error && (parsed.error.includes('Chave de API') || parsed.error.includes('API_KEY')))) {
        showToast('Configuração de IA necessária. Verifique sua chave nas configurações.', 'info');
        return;
      }

      if (parsed.error) {
        showToast(parsed.error, 'error');
        return;
      }

      setGeneratedCopy(parsed);
      markChecklistItemCompleted('copy');
      setStep('page');
      showToast('Copy gerada com sucesso!', 'success');
    } catch (error) {
      console.error('Error generating copy:', error);
      showToast('Erro ao gerar copy. Tente novamente.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePage = async () => {
    if (!auth.currentUser) return;
    
    setLoading(true);
    const path = 'landing_pages';
    try {
      const pageData = {
        userId: auth.currentUser.uid,
        name: `Lançamento: ${productName}`,
        description: description,
        status: 'published',
        url: '', // Initial empty URL
        createdAt: serverTimestamp(),
        traffic: 0,
        conversions: 0,
        content: {
          headline: generatedCopy?.headline || 'Nova Landing Page',
          ctaText: generatedCopy?.ctaText || 'Começar Agora',
          body: generatedCopy?.body || 'Descrição do seu produto aqui...'
        }
      };

      const docRef = await addDoc(collection(db, path), pageData);
      
      // Update with real URL using ID
      await updateDoc(docRef, {
        url: `${window.location.origin}/p/${docRef.id}`
      });

      setCreatedPageId(docRef.id);
      markChecklistItemCompleted('page');
      setStep('performance');
      showToast('Landing Page criada com sucesso!', 'success');
    } catch (error: any) {
      console.error('Error creating page:', error);
      
      if (error.code === 'permission-denied') {
        const errInfo = {
          error: error.message,
          operationType: 'write',
          path: path,
          authInfo: {
            userId: auth.currentUser?.uid,
            email: auth.currentUser?.email,
            emailVerified: auth.currentUser?.emailVerified,
            isAnonymous: auth.currentUser?.isAnonymous,
            providerInfo: auth.currentUser?.providerData.map(provider => ({
              providerId: provider.providerId,
              displayName: provider.displayName,
              email: provider.email,
              photoUrl: provider.photoURL
            })) || []
          }
        };
        console.error('Firestore Error: ', JSON.stringify(errInfo));
        showToast(`Erro de permissão: ${error.message}`, 'error');
      } else {
        showToast('Erro ao criar página.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    markChecklistItemCompleted('optimize');
    setStep('finish');
  };

  const renderStep = () => {
    switch (step) {
      case 'intro':
        return (
          <div className="space-y-8 py-6 text-center animate-fade-in">
            <div className="w-20 h-20 bg-indigo-500/10 rounded-3xl flex items-center justify-center text-indigo-500 mx-auto animate-bounce">
              <Rocket className="w-10 h-10" />
            </div>
            <div className="space-y-3">
              <h2 className="text-3xl font-black text-white tracking-tight">Assistente de Novo Projeto</h2>
              <p className="text-slate-400 text-sm max-w-sm mx-auto">
                Vamos transformar sua ideia em um lançamento real em 3 passos simples.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4 text-left">
              {[
                { icon: Sparkles, label: 'Copy IA', color: 'text-amber-400' },
                { icon: Layers, label: 'Página', color: 'text-emerald-400' },
                { icon: TrendingUp, label: 'Performance', color: 'text-indigo-400' }
              ].map((item, i) => (
                <div key={i} className="p-4 bg-white/5 border border-white/10 rounded-2xl space-y-2">
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                  <p className="text-[10px] font-bold text-white uppercase tracking-widest">{item.label}</p>
                </div>
              ))}
            </div>
            <button 
              onClick={() => setStep('copy')}
              className="w-full py-5 bg-indigo-500 text-white font-black rounded-2xl shadow-2xl shadow-indigo-500/40 hover:bg-indigo-400 transition-all flex items-center justify-center gap-3 group"
            >
              Começar Agora
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        );

      case 'copy':
        return (
          <div className="space-y-6 py-4 animate-fade-in">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center text-amber-500">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Passo 1: Copy de Elite</h3>
                <p className="text-xs text-slate-500">Defina seu produto para a IA escrever.</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="micro-label">Nome do Produto</label>
                <input 
                  type="text"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="Ex: Curso de Marketing Digital"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-indigo-500/50 transition-all outline-none"
                />
              </div>
              <div className="space-y-2">
                <label className="micro-label">O que seu produto resolve?</label>
                <textarea 
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Descreva os benefícios e o público-alvo..."
                  rows={4}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-indigo-500/50 transition-all outline-none resize-none"
                />
              </div>
            </div>

            <button 
              onClick={handleGenerateCopy}
              disabled={loading}
              className="w-full py-4 bg-white text-black font-bold rounded-xl hover:bg-slate-100 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Gerar Copy com IA'}
            </button>
          </div>
        );

      case 'page':
        return (
          <div className="space-y-8 py-6 text-center animate-fade-in">
            <div className="w-20 h-20 bg-emerald-500/10 rounded-3xl flex items-center justify-center text-emerald-500 mx-auto">
              <Layers className="w-10 h-10" />
            </div>
            <div className="space-y-3">
              <h3 className="text-2xl font-bold text-white">Passo 2: Criar Landing Page</h3>
              <p className="text-slate-400 text-sm">
                Sua copy está pronta! Agora vamos criar a página onde ela será exibida.
              </p>
            </div>
            
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl text-left space-y-4">
              <div className="space-y-1">
                <p className="micro-label text-emerald-400">Título Gerado</p>
                <p className="text-white font-bold leading-tight">{generatedCopy?.headline}</p>
              </div>
              <div className="space-y-1">
                <p className="micro-label text-emerald-400">Chamada para Ação (CTA)</p>
                <p className="text-white font-bold">{generatedCopy?.ctaText}</p>
              </div>
            </div>

            <button 
              onClick={handleCreatePage}
              disabled={loading}
              className="w-full py-5 bg-emerald-500 text-white font-black rounded-2xl shadow-2xl shadow-emerald-500/40 hover:bg-emerald-400 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Criar Minha Página Agora'}
            </button>
          </div>
        );

      case 'performance':
        return (
          <div className="space-y-8 py-6 text-center animate-fade-in">
            <div className="w-20 h-20 bg-indigo-500/10 rounded-3xl flex items-center justify-center text-indigo-500 mx-auto">
              <TrendingUp className="w-10 h-10" />
            </div>
            <div className="space-y-3">
              <h3 className="text-2xl font-bold text-white">Passo 3: Validar Performance</h3>
              <p className="text-slate-400 text-sm">
                Página criada! O próximo passo é ativar o monitoramento de IA para otimizar suas conversões.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-white/5 border border-white/10 rounded-2xl space-y-2 text-left">
                <Zap className="w-5 h-5 text-amber-400" />
                <p className="text-[10px] font-bold text-white uppercase tracking-widest">TurboQuant</p>
                <p className="text-[9px] text-slate-500">Monitoramento 24/7 ativado.</p>
              </div>
              <div className="p-4 bg-white/5 border border-white/10 rounded-2xl space-y-2 text-left">
                <Target className="w-5 h-5 text-indigo-400" />
                <p className="text-[10px] font-bold text-white uppercase tracking-widest">A/B Automático</p>
                <p className="text-[9px] text-slate-500">Pronto para testar variações.</p>
              </div>
            </div>

            <button 
              onClick={handleFinish}
              className="w-full py-5 bg-white text-black font-black rounded-2xl hover:bg-slate-100 transition-all flex items-center justify-center gap-3"
            >
              Ativar Otimização IA
            </button>
          </div>
        );

      case 'finish':
        return (
          <div className="space-y-8 py-6 text-center animate-fade-in">
            <div className="w-24 h-24 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-500 mx-auto border-4 border-emerald-500/20">
              <CheckCircle2 className="w-12 h-12" />
            </div>
            <div className="space-y-3">
              <h3 className="text-3xl font-black text-white tracking-tight">Tudo Pronto!</h3>
              <p className="text-slate-400 text-sm">
                Seu lançamento foi configurado com sucesso. Agora é só enviar tráfego e deixar a IA trabalhar.
              </p>
            </div>

            <div className="space-y-4">
              <button 
                onClick={() => {
                  if (onChangeView) {
                    onChangeView('landing_pages');
                  } else {
                    window.dispatchEvent(new CustomEvent('change-view', { detail: 'landing_pages' }));
                  }
                  onClose();
                }}
                className="w-full py-4 bg-white/5 hover:bg-white/10 text-white font-bold rounded-xl border border-white/10 transition-all cursor-pointer"
              >
                Ver Minhas Páginas
              </button>
              <button 
                onClick={onClose}
                className="w-full py-4 bg-indigo-500 text-white font-bold rounded-xl hover:bg-indigo-400 transition-all cursor-pointer"
              >
                Voltar ao Dashboard
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 lg:p-0 pt-10 lg:pt-20 overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />
      
      <div className="relative w-full max-w-xl premium-card overflow-hidden animate-scale-in my-8">
        <div className="absolute top-0 left-0 w-full h-1.5 bg-white/5">
          <div 
            className="h-full bg-indigo-500 transition-all duration-500"
            style={{ 
              width: step === 'intro' ? '0%' : 
                     step === 'copy' ? '25%' : 
                     step === 'page' ? '50%' : 
                     step === 'performance' ? '75%' : '100%' 
            }}
          />
        </div>

        <button 
          onClick={onClose}
          className="absolute top-6 right-6 p-2 text-slate-500 hover:text-white transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8 lg:p-12">
          {renderStep()}
        </div>

        {step !== 'intro' && step !== 'finish' && (
          <div className="px-8 pb-8 flex justify-between items-center">
            <button 
              onClick={() => {
                if (step === 'copy') setStep('intro');
                if (step === 'page') setStep('copy');
                if (step === 'performance') setStep('page');
              }}
              className="micro-label flex items-center gap-2 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-3 h-3" /> Voltar
            </button>
            <div className="flex gap-1">
              {['copy', 'page', 'performance'].map((s) => (
                <div 
                  key={s}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${step === s ? 'bg-indigo-500 w-4' : 'bg-white/10'}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
